import type {
  ICalloutContentStyleProps,
  ICalloutContentStyles,
} from "@fluentui/react/lib/Callout";
import type {
  IContextualMenuStyles,
  IMenuItemStyles,
} from "@fluentui/react/lib/ContextualMenu";
import type { ITheme } from "@fluentui/react/lib/Styling";
import {
  FontWeights,
  getTheme,
  HighContrastSelector,
  mergeStyleSets,
} from "@fluentui/react/lib/Styling";
import type { IStyleFunctionOrObject } from "@fluentui/react/lib/Utilities";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import type {
  IContextualMenuClassNames,
  IMenuItemClassNames,
  StandardControlSemanticColors,
} from "@1js/acui-common";
import {
  commonCalloutBoxShadow,
  commonHighContrastBorder,
  controlPalette,
  getStandardControlSemanticColorsWithTheme,
} from "@1js/acui-common";
import { isThemingEnabled } from "@1js/acui-theme";

export enum MenuSectionStyleType {
  Normal = 0,
  Footer = 1,
  Flex = 2,
  Header = 3,
}

const title = (theme: ITheme) => {
  const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
  return {
    item: {
      backgroundColor: semanticColors.menuBackgroundColor,
    },
    label: {
      color: isThemingEnabled()
        ? semanticColors.menuItemTextColor
        : controlPalette.menuItemTextColor,
    },
  };
};
const divider = (theme: ITheme, sectionType?: MenuSectionStyleType) => {
  const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
  return {
    divider: {
      margin:
        sectionType === MenuSectionStyleType.Footer
          ? "3px 18px"
          : sectionType === MenuSectionStyleType.Flex
            ? "8px 0px 8px 18px"
            : "3px 8px",
      backgroundColor: isThemingEnabled()
        ? !sectionType
          ? semanticColors.menuDividerBackgroundColor
          : semanticColors.borderColor
        : "#E5E5E5",
      display: "block",
      height: sectionType === MenuSectionStyleType.Footer ? 0.8 : 1,
      position: "relative",
      [HighContrastSelector]: {
        backgroundColor: "ButtonText",
      },
    },
  };
};

export const getMenuTitleClassNames = memoizeFunction(
  (theme: ITheme): IMenuItemClassNames => {
    return mergeStyleSets(
      {
        divider: {},
        root: {},
        linkContent: {},
        icon: {},
        checkmarkIcon: {},
        secondaryText: {},
        subMenuIcon: {},
        splitContainer: {},
        splitPrimary: {},
        splitMenu: {},
        linkContentMenu: {},
        screenReaderText: {},
      },
      title(theme)
    );
  }
);

export const getMenuSectionClassNames = memoizeFunction(
  (
    customDividerStyle?: Partial<IMenuItemStyles>,
    sectionType?: MenuSectionStyleType
  ) => {
    return (theme: ITheme): IMenuItemClassNames => {
      return mergeStyleSets(
        {
          root: {},
          linkContent: {},
          icon: {},
          checkmarkIcon: {},
          secondaryText: {},
          subMenuIcon: {},
          splitContainer: {},
          splitPrimary: {},
          splitMenu: {},
          linkContentMenu: {},
          screenReaderText: {},
        },
        title(theme),
        divider(theme, sectionType),
        customDividerStyle
      );
    };
  }
);

export const flexMenuStyles: IContextualMenuStyles = {
  container: {
    overflow: "hidden",
    maxHeight: "100%",
    display: "flex",
    padding: 0,
  },
  root: {
    display: "flex",
  },
  list: {},
  header: {
    paddingLeft: 18,
  },
  title: {},
  subComponentStyles: {
    callout: {},
    menuItem: {},
  },
};

export const flexMenuCalloutContentStyles: Partial<ICalloutContentStyles> = {
  calloutMain: {
    overflow: "hidden",
    minWidth: "fit-content",
  },
  root: {
    maxWidth: "fit-content",
  },
};

export const getMenuFooterStyles = (
  theme: ITheme = getTheme(),
  itemCount?: number,
  itemIndex?: number
): IMenuItemStyles => {
  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  const isRTL = theme.rtl;
  const emptyStyle = {};
  const linkStyle = {
    root: {
      float: "left",
      border: "none",
      background: "none !important",
      color: theme.palette.themePrimary ?? "#0078D4",
      cursor: "pointer",
      fontSize: "12px",
      marginBottom: "2px",
      marginLeft: "-11px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      ":hover": {
        textDecoration: "underline",
        cursor: "pointer",
      },
      ":focus": {},
      "& i": {
        display: "none !important",
      },
    },
    linkContent: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      height: "32px",
      backgroundColor: "transparent",
      fontSize: "12px",
      fontWeight: "600",
      paddingRight: "4px",
    },
    label: {
      flexGrow: 0,
      lineHeight: "unset",
    },
    icon: {
      width: "16px",
      height: "16px",
      display: "flex",
      padding: "0",
    },
  };
  const selectedStyle =
    itemCount === 2 && itemIndex === 0 ? linkStyle : emptyStyle;
  return mergeStyleSets(
    {
      item: {
        direction: isRTL ? "ltr" : "rtl",
        margin: "16px 18px",
        float:
          itemCount === 2 ? (itemIndex === 0 ? "left" : "right") : undefined,
      },
      root: {
        position: "relative",
        direction: isRTL ? "rtl" : "ltr",
        padding: "0px 12px",
        height: "unset",
        width: "unset",
        borderRadius: 4,
        border: `1px solid ${standardControlSemanticColors.borderColor}`,
      },
      linkContent: {
        display: "flex",
        gap: "6px",
        height: 32,
        backgroundColor: "transparent",
        fontSize: 13,
        fontWeight: 600,
        position: "relative",
        alignItems: "center",
        paddingRight: 4,
      },
      label: {
        flexGrow: 0,
        lineHeight: "unset",
      },
      icon: {
        width: "16px",
        height: "16px",
        display: "flex",
        padding: "0",
      },
      checkmarkIcon: {},
      secondaryText: {},
      subMenuIcon: {},
      screenReaderText: {},
    },
    selectedStyle
  );
};

export const getMenuDividerClassNames = memoizeFunction(
  (customDividerStyle?: Partial<IMenuItemStyles>) => {
    return (theme: ITheme): IMenuItemClassNames => {
      return mergeStyleSets(
        {
          item: {},
          label: {},
          root: {},
          linkContent: {},
          icon: {},
          checkmarkIcon: {},
          secondaryText: {},
          subMenuIcon: {},
          splitContainer: {},
          splitPrimary: {},
          splitMenu: {},
          linkContentMenu: {},
          screenReaderText: {},
        },
        divider(theme),
        customDividerStyle
      );
    };
  }
);

export const getMenuItemStyles = memoizeFunction(
  (
    customMenuItemStyle?: Partial<IMenuItemStyles>
  ): Partial<IMenuItemStyles> => {
    return mergeStyleSets(
      {
        item: {},
        label: {},
        divider: {},
        root: {},
        linkContent: {},
        icon: {},
        checkmarkIcon: {},
        secondaryText: {},
        subMenuIcon: {},
        splitContainer: {},
        splitPrimary: {},
        splitMenu: {},
        linkContentMenu: {},
      },
      customMenuItemStyle
    );
  }
);

export const getMenuClassNames = (
  customMenuClassNames?: IContextualMenuClassNames,
  responsiveClassName?: string
) => {
  return memoizeFunction((): IContextualMenuClassNames => {
    // Fabric issue #6922: there's a bug with concatStyleSets and mergeStyles that
    // gives the wrong type for IContextualMenuClassName
    let contextualMenuClassNames = {
      ...mergeStyleSets(
        {
          container: [
            {
              whiteSpace: "nowrap",
              paddingTop: 4,
              paddingBottom: 4,
              selectors: {
                ":focus": { outline: 0 },
              },
            },
            commonHighContrastBorder,
          ],
          list: {
            listStyleType: "none",
            margin: 0,
            padding: 0,
            lineHeight: 0,
          },
          // Styles to apply to both menu section titles and the menu title
          header: {
            fontSize: 14,
            fontWeight: FontWeights.semibold,
            padding: "0px 12px",
            userSelect: "none",
            height: 32,
            lineHeight: 32,
            cursor: "default",
          },
          root: { overflow: "hidden" },
          title: {},
        },
        customMenuClassNames
      ),
      subComponentStyles: { menuItem: {}, callout: {} },
    };
    if (responsiveClassName) {
      contextualMenuClassNames = {
        ...contextualMenuClassNames,
        root: `${contextualMenuClassNames.root} ${responsiveClassName}`,
      };
    }
    return contextualMenuClassNames;
  });
};

export type GetMenuCalloutStylesResult = IStyleFunctionOrObject<
  ICalloutContentStyleProps,
  ICalloutContentStyles
>;

let menuBackgroundColor: string = "";

export const getMenuCalloutStyles: (
  theme: ITheme,
  customMenuClassNames?: IStyleFunctionOrObject<
    ICalloutContentStyleProps,
    ICalloutContentStyles
  >,
  isSubmenuThemeColorFixEnabled?: boolean
) => IStyleFunctionOrObject<ICalloutContentStyleProps, ICalloutContentStyles> =
  memoizeFunction(
    (
      theme: ITheme,
      customMenuClassNames?: IStyleFunctionOrObject<
        ICalloutContentStyleProps,
        ICalloutContentStyles
      >,
      isSubmenuThemeColorFixEnabled?: boolean
    ): IStyleFunctionOrObject<
      ICalloutContentStyleProps,
      ICalloutContentStyles
    > => {
      if (isSubmenuThemeColorFixEnabled) {
        menuBackgroundColor =
          getStandardControlSemanticColorsWithTheme(theme).menuBackgroundColor;
        const mergedStyles: GetMenuCalloutStylesResult = (
          props: ICalloutContentStyleProps
        ): Partial<ICalloutContentStyles> => {
          const calloutStyles = getCalloutStyles(menuBackgroundColor);
          return mergeStyleSets(
            typeof customMenuClassNames === "function"
              ? customMenuClassNames(props)
              : customMenuClassNames,
            calloutStyles
          );
        };
        return mergedStyles;
      }
      const backgroundColor =
        getStandardControlSemanticColorsWithTheme(theme).menuBackgroundColor;
      const calloutStyles: Partial<ICalloutContentStyles> = {
        root: [commonCalloutBoxShadow, { border: "none" }],
        calloutMain: [
          {
            // The Fabric ContextualMenu explicitly removes 2px from the width of the callout
            // to account for the border. Since our menu doesn't have a border in non high contrast mode, we need to add those 2px back.
            width: "calc(100% + 2px)",
            selectors: {
              // Since our menu does have border in High Contrast  mode, we don't add 2px back in High Contrast mode.
              [HighContrastSelector]: {
                width: "100%",
              },
            },
          },
          isThemingEnabled() && { backgroundColor },
        ],
      };
      const mergedStyles: GetMenuCalloutStylesResult = (
        props: ICalloutContentStyleProps
      ): Partial<ICalloutContentStyles> => {
        return mergeStyleSets(
          typeof customMenuClassNames === "function"
            ? customMenuClassNames(props)
            : customMenuClassNames,
          calloutStyles
        );
      };
      return mergedStyles;
    }
  );

const getCalloutStyles = memoizeFunction(
  (backgroundColor: string): Partial<ICalloutContentStyles> => {
    return {
      root: [commonCalloutBoxShadow, { border: "none" }],
      calloutMain: [
        {
          // The Fabric ContextualMenu explicitly removes 2px from the width of the callout
          // to account for the border. Since our menu doesn't have a border in non high contrast mode, we need to add those 2px back.
          width: "calc(100% + 2px)",
          selectors: {
            // Since our menu does have border in High Contrast  mode, we don't add 2px back in High Contrast mode.
            [HighContrastSelector]: {
              width: "100%",
            },
          },
        },
        isThemingEnabled() && { backgroundColor },
      ],
    };
  }
);
